// Mapping of technical proof names to human-readable labels
const PROOF_LABELS = {
  // New schema format
  "name": "Legal Name",
  "phone_number": "Phone Number",
  "pg_region": "Geographic Region",
  "pg_attestation_key_id": "Hardware Verification",
  "pg_photo_verification_url": "Real-time Selfie",
  "pg_presence": "Presence Verification",
  "pg_presence_scoring": "Presence Scoring",
  "pg_high_certainty": "Step-up Document Check",
  "pg_region_geofence": "Geofence",
  "pg_guest_list": "Guest List",
  "pg_age": "Current Age",
  "pg_document_type": "Document Type",
  "pg_verification_id": "Verification ID",
  "pg_verified_by_provider": "Verified By Provider",
  "pg_issuing_country": "Issuing Country",
  "full_name": "Legal Name",
  "region": "Geographic Region",
  "hardware_attestation_id": "Hardware Verification",
};

// i18n key mapping — used by components that have access to a t() function
export const PROOF_LABEL_KEYS = {
  "name": "proofLabels.name",
  "phone_number": "proofLabels.phone_number",
  "pg_region": "proofLabels.pg_region",
  "pg_attestation_key_id": "proofLabels.pg_attestation_key_id",
  "pg_photo_verification_url": "proofLabels.pg_photo_verification_url",
  "pg_presence": "proofLabels.pg_presence",
  "pg_presence_scoring": "proofLabels.pg_presence_scoring",
  "pg_high_certainty": "proofLabels.pg_high_certainty",
  "pg_region_geofence": "proofLabels.pg_region_geofence",
  "pg_guest_list": "proofLabels.pg_guest_list",
  "pg_age": "proofLabels.pg_age",
  "pg_document_type": "proofLabels.pg_document_type",
  "pg_verification_id": "proofLabels.pg_verification_id",
  "pg_verified_by_provider": "proofLabels.pg_verified_by_provider",
  "pg_issuing_country": "proofLabels.pg_issuing_country",
  "full_name": "proofLabels.name",
  "region": "proofLabels.pg_region",
  "hardware_attestation_id": "proofLabels.pg_attestation_key_id",
};

/**
 * Format a proof name to a human-readable label.
 * If a `t` function is provided (from useTranslation), uses i18n keys.
 * Otherwise falls back to English labels.
 */
export const formatProofLabel = (proofName, t) => {
  if (!proofName || typeof proofName !== 'string') {
    return proofName;
  }

  // If t() is provided, use i18n
  if (t && PROOF_LABEL_KEYS[proofName]) {
    return t(PROOF_LABEL_KEYS[proofName]);
  }

  // Check if we have a direct mapping
  if (PROOF_LABELS[proofName]) {
    return PROOF_LABELS[proofName];
  }

  // Fallback: Convert snake_case to Title Case and remove common prefixes
  return proofName
    .replace(/^pg_/, '') // Remove 'pg_' prefix
    .replace(/^psl_/, '') // Remove 'psl_' prefix
    .replace(/_/g, ' ') // Replace underscores with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};


export const formatProofLabels = (proofs, t) => {
  if (!Array.isArray(proofs)) {
    return proofs;
  }

  return proofs.map(p => formatProofLabel(p, t));
};

// Single source of truth for selectable required proofs. Consumers render
// from this so the available options never drift between dialogs.
export const AVAILABLE_PROOFS = [
  { key: 'name' },
  { key: 'pg_presence' },
  { key: 'pg_presence_scoring', descriptionKey: 'proofDescriptions.pg_presence_scoring' },
  { key: 'pg_region' },
  { key: 'pg_photo_verification_url' },
  { key: 'phone_number' },
  { key: 'pg_age' },
  { key: 'pg_region_geofence', descriptionKey: 'proofDescriptions.pg_region_geofence' },
  { key: 'pg_high_certainty', descriptionKey: 'proofDescriptions.pg_high_certainty' },
  { key: 'pg_guest_list', descriptionKey: 'proofDescriptions.pg_guest_list' },
  { key: 'pg_document_type' },
  { key: 'pg_verification_id' },
  { key: 'pg_verified_by_provider' },
  { key: 'pg_issuing_country' },
];

// Virtual proofs that imply their dependencies; a virtual proof is only allowed when every dependency is also allowed.
export const PROOF_DEPENDENCIES = {
  pg_high_certainty: ['name', 'pg_region'],
  pg_region_geofence: ['pg_region'],
  pg_guest_list: ['name'],
  pg_presence_scoring: ['pg_presence'],
  // Document attributes ride along with the government-ID proof, which mobile
  // only bundles when Legal Name is required.
  pg_document_type: ['name'],
  pg_verification_id: ['name'],
  pg_verified_by_provider: ['name'],
  pg_issuing_country: ['name'],
};

// Checks if a proof key is allowed. `allowedProofs` null/undefined means unrestricted.
export const isProofAllowed = (key, allowedProofs) => {
  if (!allowedProofs) return true;
  if (!AVAILABLE_PROOFS.some((p) => p.key === key)) return true;
  if (!allowedProofs.includes(key)) return false;
  return (PROOF_DEPENDENCIES[key] || []).every((dep) => allowedProofs.includes(dep));
};
